/**
 * Novanalyse — the ecosystem strip (auth canvas, §4).
 *
 * The four-pillar lockup — Novanalyse (active) · Novera Med · Pharmakon · Ailaji — shown at the
 * bottom of the brand canvas. It is itself a trust signal: Novanalyse is part of a real,
 * multi-product platform, not an isolated tool.
 *
 * No official marks were supplied for the sibling products, so we author four minimal single-color
 * glyphs in the Novera visual language (currentColor, the 100×100 viewBox + rounded joins of the
 * brand mark). A later swap for real assets is a drop-in here — the only place these glyphs live.
 */
import { NovaMark } from "./brand";
import { ECOSYSTEM, type EcosystemKey } from "./brand.config";

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Minimal authored glyphs (geometric, no literal clichés). */
function EcosystemGlyph({ pillar, size = 26 }: { pillar: EcosystemKey; size?: number }) {
  if (pillar === "novanalyse") {
    // the Novanalyse mark itself (droplet + validated tick)
    return <NovaMark size={size} tone="glyph" />;
  }
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 100 100",
    role: "img" as const,
    "aria-hidden": true,
  };
  if (pillar === "novera") {
    // "Rising V / Nova": upward chevron + detached nova dot
    return (
      <svg {...common}>
        <path d="M27 64 L50 41 L73 64" {...STROKE} />
        <circle cx="50" cy="24" r="7.6" fill="currentColor" />
      </svg>
    );
  }
  if (pillar === "pharmakon") {
    // mortar (bowl + rim) + pestle bar — pharmacy, abstracted
    return (
      <svg {...common}>
        <path d="M28 50 a22 22 0 0 0 44 0" {...STROKE} />
        <path d="M24 50 h52" {...STROKE} />
        <path d="M50 72 v8" {...STROKE} />
        <path d="M60 22 l12 16" {...STROKE} strokeWidth={6.5} />
      </svg>
    );
  }
  // ailaji — care monogram: shield + pulse
  return (
    <svg {...common}>
      <path d="M50 18 l26 9 v20 c0 19-15 30-26 34 c-11-4-26-15-26-34 V27 Z" {...STROKE} />
      <path d="M36 47 h8 l4 9 l6-16 l4 7 h6" {...STROKE} strokeWidth={6} />
    </svg>
  );
}

export function EcosystemStrip() {
  return (
    <div className="nv-eco">
      <div className="nv-eco__eyebrow">L'écosystème Novera</div>
      <ul className="nv-eco__row">
        {ECOSYSTEM.map((p) => (
          <li key={p.key} className={`nv-eco__tile${p.active ? " is-on" : ""}`} title={p.name}>
            <span className="nv-eco__glyph" aria-hidden>
              <EcosystemGlyph pillar={p.key} />
            </span>
            <span className="nv-eco__txt">
              <span className="nv-eco__name">{p.name}</span>
              <span className="nv-eco__role">{p.role}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export { EcosystemGlyph };
